import React, { useState, useEffect } from 'react';
import { WarningIcon } from './SvgConsolidated/SvgIcons';

// Get backend URL from environment - use backend directly, not frontend proxy
const getBackendUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL || 'https://learning-companion-be-dev.data-nonprod.edgenuity.com';
};

interface TableInfo {
  name: string;
  row_count: number;
}

interface Migration {
  filename: string;
  checksum: string;
  applied_at: string;
  execution_time_ms: number;
  status: string;
  error_message: string | null;
}

interface DatabaseStatus {
  database_type: string;
  database_path: string;
  database_size_bytes: number;
  database_size_mb: number;
  database_size_kb: number;
  total_tables: number;
  total_indexes: number;
  total_migrations: number;
  tables: TableInfo[];
  migrations: Migration[];
  indexes: string[];
  initialization_info?: {
    initialized: boolean;
    initialization_time: string | null;
    type: string;
    path: string;
    error: string | null;
    tables_created: number;
    migrations_applied: number;
    schema_version: string | null;
  };
  status: string;
  error?: string;
}

interface HealthCheck {
  overall_status: string;
  database_type: string;
  database_path: string;
  initialization_info?: any;
  checks: {
    connection: { status: string; details: string };
    integrity: { status: string; details: string };
    critical_tables: { status: string; details: string };
    migrations: { status: string; details: string };
  };
  timestamp: number;
  error?: string;
}

interface TableData {
  table_name: string;
  total_rows: number;
  returned_rows: number;
  offset: number;
  limit: number;
  columns: string[];
  data: Record<string, any>[];
}

const DatabaseStatusViewer: React.FC = () => {
  const [dbStatus, setDbStatus] = useState<DatabaseStatus | null>(null);
  const [healthCheck, setHealthCheck] = useState<HealthCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'tables' | 'migrations' | 'data'>('overview');
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [dataLoading, setDataLoading] = useState(false);

  const fetchDatabaseStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use backend URL directly instead of frontend proxy
      const backendUrl = getBackendUrl();
      
      const [statusResponse, healthResponse] = await Promise.all([
        fetch(`${backendUrl}/database/status`),
        fetch(`${backendUrl}/database/health`).catch(() => null)
      ]);
      
      if (!statusResponse.ok) {
        throw new Error(`HTTP error! status: ${statusResponse.status}`);
      }
      
      const statusData: DatabaseStatus = await statusResponse.json();
      setDbStatus(statusData);
      
      if (healthResponse && healthResponse.ok) {
        const healthData: HealthCheck = await healthResponse.json();
        setHealthCheck(healthData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch database status');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (tableName: string, offset: number = 0, limit: number = 100) => {
    setDataLoading(true);
    
    try {
      // Use backend URL directly instead of frontend proxy
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/database/tables/${tableName}/data?limit=${limit}&offset=${offset}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: TableData = await response.json();
      setTableData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch table data');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchDatabaseStatus();
  }, []);

  useEffect(() => {
    if (selectedTable && selectedTab === 'data') {
      fetchTableData(selectedTable);
    }
  }, [selectedTable, selectedTab]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      healthy: 'bg-green-100 text-green-800',
      ok: 'bg-green-100 text-green-800',
      success: 'bg-green-100 text-green-800',
      unhealthy: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      error: 'bg-red-100 text-red-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  if (loading && !dbStatus) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 font-medium">Error: {error}</p>
          <button
            onClick={fetchDatabaseStatus}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dbStatus) return null;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Database Status</h3>
          <div className="flex gap-2">
            {dbStatus && getStatusBadge(dbStatus.status)}
            {healthCheck && getStatusBadge(healthCheck.overall_status)}
            <button
              onClick={fetchDatabaseStatus}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-4">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'tables', label: 'Tables' },
              { id: 'migrations', label: 'Migrations' },
              { id: 'data', label: 'Table Data' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`px-4 py-2 border-b-2 font-medium text-sm ${
                  selectedTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Overview Tab */}
      {selectedTab === 'overview' && (
        <div className="space-y-4">
          {/* Database Type and Connection Info */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Database Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-600">Type</p>
                <p className="text-lg font-bold text-blue-600 uppercase">{dbStatus.database_type || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Path</p>
                <p className="text-sm font-mono text-gray-800 break-all">{dbStatus.database_path}</p>
              </div>
              {dbStatus.initialization_info && (
                <>
                  <div>
                    <p className="text-xs text-gray-600">Initialized</p>
                    <p className="text-sm font-medium">
                      {dbStatus.initialization_info.initialized ? (
                        <span className="text-green-600">✅ Yes</span>
                      ) : (
                        <span className="text-red-600">❌ No</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Initialization Time</p>
                    <p className="text-sm text-gray-700">
                      {dbStatus.initialization_info.initialization_time 
                        ? new Date(dbStatus.initialization_info.initialization_time).toLocaleString()
                        : 'N/A'}
                    </p>
                  </div>
                  {dbStatus.initialization_info.error && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Error</p>
                      <p className="text-sm text-red-600 font-mono">{dbStatus.initialization_info.error}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Database Size</p>
              <p className="text-2xl font-bold text-blue-600">
                {dbStatus.database_size_mb} MB
              </p>
              <p className="text-xs text-gray-500">
                {dbStatus.database_size_kb} KB ({formatBytes(dbStatus.database_size_bytes)})
              </p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Tables</p>
              <p className="text-2xl font-bold text-green-600">{dbStatus.total_tables}</p>
              <p className="text-xs text-gray-500">{dbStatus.total_indexes} indexes</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Migrations</p>
              <p className="text-2xl font-bold text-purple-600">{dbStatus.total_migrations}</p>
              <p className="text-xs text-gray-500">Applied successfully</p>
            </div>
          </div>
          
          {/* Important Notice for In-Memory Database */}
          {dbStatus.database_type === 'memory' && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <WarningIcon className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">In-Memory Database</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>This database is stored in RAM and is <strong>ephemeral</strong>. All data will be lost when the application restarts.</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>✅ Fast performance - no disk I/O</li>
                      <li>✅ Perfect for stateless deployments</li>
                      <li>✅ Automatically initialized on startup</li>
                      <li>⚠️ Data does not persist across restarts</li>
                      <li>⚠️ Shared connection for multi-threaded access</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {healthCheck && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-3">Health Checks</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(healthCheck.checks).map(([key, check]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-white rounded">
                    <div className="flex-1">
                      <span className="text-sm font-medium capitalize">{key.replace('_', ' ')}</span>
                      <p className="text-xs text-gray-500 mt-1">{check.details}</p>
                    </div>
                    <div className="ml-2">{getStatusBadge(check.status)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tables Tab */}
      {selectedTab === 'tables' && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
            <div>Table Name</div>
            <div className="text-right">Row Count</div>
          </div>
          {dbStatus.tables.map((table) => (
            <div
              key={table.name}
              className="grid grid-cols-2 gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
              onClick={() => {
                setSelectedTable(table.name);
                setSelectedTab('data');
              }}
            >
              <div className="font-mono text-sm text-blue-600 hover:underline">
                {table.name}
              </div>
              <div className="text-right text-gray-600">
                {table.row_count.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Migrations Tab */}
      {selectedTab === 'migrations' && (
        <div className="space-y-3">
          {dbStatus.migrations.map((migration, index) => (
            <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-mono text-sm font-medium">{migration.filename}</p>
                  <p className="text-xs text-gray-500">{formatDate(migration.applied_at)}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(migration.status)}
                  <span className="text-xs text-gray-500">{migration.execution_time_ms}ms</span>
                </div>
              </div>
              {migration.error_message && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                  {migration.error_message}
                </div>
              )}
              <div className="mt-2 text-xs text-gray-400 font-mono truncate">
                {migration.checksum}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table Data Tab */}
      {selectedTab === 'data' && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-700">Select Table:</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a table...</option>
              {dbStatus.tables.map((table) => (
                <option key={table.name} value={table.name}>
                  {table.name} ({table.row_count} rows)
                </option>
              ))}
            </select>
          </div>
          
          {dataLoading && (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading table data...</p>
            </div>
          )}
          
          {tableData && !dataLoading && (
            <div>
              <div className="mb-2 text-sm text-gray-600">
                Showing {tableData.returned_rows} of {tableData.total_rows.toLocaleString()} rows
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {tableData.columns.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableData.data.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {tableData.columns.map((col) => (
                          <td key={col} className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {row[col] !== null ? String(row[col]) : <span className="text-gray-400">NULL</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatabaseStatusViewer;
